import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Trophy, Clock, IceCream, ChevronUp } from 'lucide-react';
import { fetchSundaeLeaderboard } from '../../services/sundaeApi';
import { useLanguage } from '../../contexts/LanguageContext';

const fmt = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Date-scoped Sundae leaderboard. Sorts by sundae_score desc, then by
 * total_elapsed_ms asc (faster wins ties). Used in two places:
 *  1) Inline in SundaeShareCard right after a user finishes
 *  2) Standalone via /community?sundae=YYYY-MM-DD (group mode link)
 */
const SundaeLeaderboard = ({ language = 'en', date, onCollapse }) => {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSundaeLeaderboard(date, 20)
      .then((r) => {
        if (!cancelled) setEntries(r.entries || []);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  return (
    <Card className="border" data-testid="sundae-leaderboard">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IceCream className="w-4 h-4 text-pink-600" />
            <h3 className="text-sm font-bold">
              {t('games.sundaeLb.title')}
            </h3>
            <Badge variant="outline" className="text-[10px]">{date}</Badge>
          </div>
          {onCollapse && (
            <Button
              onClick={onCollapse}
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground"
              data-testid="sundae-leaderboard-collapse"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6 text-muted-foreground text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('games.loading')}
          </div>
        )}

        {!loading && entries.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            {t('games.sundaeLb.empty')}
          </p>
        )}

        {!loading && entries.length > 0 && (
          <div className="space-y-1">
            {entries.map((e, i) => (
              <div
                key={e.user_id}
                className={`flex items-center gap-3 px-2 py-1.5 rounded-md text-sm ${
                  i === 0
                    ? 'bg-yellow-100 dark:bg-yellow-900/30'
                    : i === 1
                      ? 'bg-zinc-100 dark:bg-zinc-800/40'
                      : i === 2
                        ? 'bg-orange-100 dark:bg-orange-900/30'
                        : ''
                }`}
                data-testid={`sundae-lb-row-${i + 1}`}
              >
                <span className="text-xs font-mono w-5 text-muted-foreground tabular-nums">
                  {i + 1}
                </span>
                <span className="flex-1 truncate">
                  {e.name || `${e.user_id.slice(0, 8)}…`}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
                  <Clock className="w-3 h-3" />
                  {fmt(e.total_elapsed_ms)}
                </span>
                <span
                  className="flex items-center gap-1 font-bold tabular-nums w-[68px] justify-end"
                  data-testid={`sundae-lb-score-${i + 1}`}
                >
                  <Trophy className="w-3 h-3 text-yellow-500" />
                  +{e.sundae_score}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SundaeLeaderboard;
